import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BRAND } from "@/lib/brand";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Companies from "@/pages/Companies";
import Vehicles from "@/pages/Vehicles";
import Drivers from "@/pages/Drivers";
import DriverTraining from "@/pages/DriverTraining";
import DriverFile from "@/pages/DriverFile";
import Customers from "@/pages/Customers";
import Rides from "@/pages/Rides";
import Calendar from "@/pages/Calendar";
import RideImport from "@/pages/RideImport";
import ApiKeys from "@/pages/ApiKeys";
import RecurringRides from "@/pages/RecurringRides";
import Billing from "@/pages/Billing";
import Claims from "@/pages/Claims";
import DriverPay from "@/pages/DriverPay";
import AlertHistory from "@/pages/AlertHistory";
import Scorecard from "@/pages/Scorecard";
import Settings from "@/pages/Settings";
import SettingsAgreements from "@/pages/SettingsAgreements";
import FleetLive from "@/pages/FleetLive";
import Compliance from "@/pages/Compliance";
import ExportTemplates from "@/pages/ExportTemplates";
import Audit from "@/pages/Audit";
import Security from "@/pages/Security";
import Prospects from "@/pages/Prospects";
import Tenants from "@/pages/Tenants";
import Subscription from "@/pages/Subscription";
import DriverPortal from "@/pages/DriverPortal";
import DriverDocuments from "@/pages/DriverDocuments";
import DriverOnboarding from "@/pages/DriverOnboarding";
import InviteAccept from "@/pages/InviteAccept";
import RiderRating from "@/pages/RiderRating";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Account from "@/pages/Account";
import DriverAccount from "@/pages/DriverAccount";
import DriverW9 from "@/pages/DriverW9";
import DriverLicense from "@/pages/DriverLicense";
import DriverApplication from "@/pages/DriverApplication";
import DriverAgreements from "@/pages/DriverAgreements";
import DriverSubscriptionEnded from "@/pages/DriverSubscriptionEnded";
import { Toaster } from "sonner";

function Loading() {
  return <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">Loading…</div>;
}

function AgencyOnly({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "driver") return <Navigate to="/driver" replace />;
  if (user.mfa_required && location.pathname !== "/app/account") {
    return <Navigate to="/app/account?mfa=required" replace />;
  }
  return children;
}

function PlatformOnly({ children }) {
  const { user } = useAuth();
  if (!user?.is_platform || user?.role !== "agency_admin") return <Navigate to="/app" replace />;
  return children;
}

function DriverOnly({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "driver") return <Navigate to="/app" replace />;
  if (user.mfa_required && location.pathname !== "/driver/account") {
    return <Navigate to="/driver/account?mfa=required" replace />;
  }
  return children;
}

function App() {
  useEffect(() => {
    document.title = BRAND;
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        <Routes>
          <Route path="/" element={<Navigate to="/app" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot" element={<ForgotPassword />} />
          <Route path="/reset/:token" element={<ResetPassword />} />
          <Route path="/invite/:token" element={<InviteAccept />} />
          <Route path="/rate/:token" element={<RiderRating />} />
          <Route path="/driver" element={<DriverOnly><DriverPortal /></DriverOnly>} />
          <Route path="/driver/account" element={<DriverOnly><DriverAccount /></DriverOnly>} />
          <Route path="/driver/documents" element={<DriverOnly><DriverDocuments /></DriverOnly>} />
          <Route path="/driver/onboarding" element={<DriverOnly><DriverOnboarding /></DriverOnly>} />
          <Route path="/driver/w9" element={<DriverOnly><DriverW9 /></DriverOnly>} />
          <Route path="/driver/license" element={<DriverOnly><DriverLicense /></DriverOnly>} />
          <Route path="/driver/application" element={<DriverOnly><DriverApplication /></DriverOnly>} />
          <Route path="/driver/agreements" element={<DriverOnly><DriverAgreements /></DriverOnly>} />
          <Route path="/driver/subscription-ended" element={<DriverOnly><DriverSubscriptionEnded /></DriverOnly>} />
          <Route path="/app" element={<AgencyOnly><Layout /></AgencyOnly>}>
            <Route index element={<Dashboard />} />
            <Route path="companies" element={<Companies />} />
            <Route path="vehicles" element={<Vehicles />} />
            <Route path="drivers" element={<Drivers />} />
            <Route path="drivers/:id/training" element={<DriverTraining />} />
            <Route path="drivers/:id/file" element={<DriverFile />} />
            <Route path="customers" element={<Customers />} />
            <Route path="rides" element={<Rides />} />
            <Route path="calendar" element={<Calendar />} />
            <Route path="fleet" element={<FleetLive />} />
            <Route path="recurring" element={<RecurringRides />} />
            <Route path="import" element={<RideImport />} />
            <Route path="api-keys" element={<ApiKeys />} />
            <Route path="billing" element={<Billing />} />
            <Route path="claims" element={<Claims />} />
            <Route path="driver-pay" element={<DriverPay />} />
            <Route path="alerts" element={<AlertHistory />} />
            <Route path="scorecard" element={<Scorecard />} />
            <Route path="settings" element={<Settings />} />
            <Route path="settings/agreements" element={<SettingsAgreements />} />
            <Route path="compliance" element={<Compliance />} />
            <Route path="exports" element={<ExportTemplates />} />
            <Route path="audit" element={<Audit />} />
            <Route path="security" element={<Security />} />
            <Route path="prospects" element={<PlatformOnly><Prospects /></PlatformOnly>} />
            <Route path="tenants" element={<PlatformOnly><Tenants /></PlatformOnly>} />
            <Route path="subscription" element={<Subscription />} />
            <Route path="account" element={<Account />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
